/**
 * Repository Service
 * Handles all repository-related API calls to backend
 * Replaces direct GitHub/GitLab API calls

 */

import axios from 'axios';
import { Repository } from '../types';
import { api } from './authenticatedApi';
import { API_CONFIG } from '../config/api.config';
import { logger } from '../utils/logger';

export interface OAuthConnection {
  id: string;
  provider: 'github' | 'gitlab';
  username: string;
  email?: string;
  avatarUrl?: string;
  scopes: string[];
  lastSyncedAt: string;
  createdAt: string;
}

export interface SyncResult {
  message: string;
  synced: number;
  provider?: string;
  errors?: Array<{ provider: string; error: string }>;
}

export interface RepositoryListResponse {
  repositories: Repository[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface OrganizationInfo {
  login: string;
  avatarUrl: string | null;
  accessStatus: 'active' | 'restricted' | 'blocked';
  repoCount: number;
}

export interface OrganizationsResponse {
  organizations: OrganizationInfo[];
  personalAccount: {
    login: string;
    repoCount: number;
  };
  githubOAuthClientId?: string;
}

export enum ConnectionStatus {
  CONNECTED = 'connected',
  CONNECTING = 'connecting',
  DISCONNECTED = 'disconnected',
  ERROR = 'error',
}

class RepositoryServiceClass {
  private connectionStatus: ConnectionStatus = ConnectionStatus.DISCONNECTED;
  private consecutiveFailures = 0;
  private activeSyncs: Map<string, Promise<SyncResult>> = new Map();

  /**
   * Get current connection status
   */
  getConnectionStatus(): ConnectionStatus {
    return this.connectionStatus;
  }

  /**
   * Check if backend is reachable
   */
  async checkConnection(): Promise<boolean> {
    try {
      this.connectionStatus = ConnectionStatus.CONNECTING;

      const response = await api.get('/health', { timeout: 5000 });

      if (response.status === 200) {
        this.connectionStatus = ConnectionStatus.CONNECTED;
        this.consecutiveFailures = 0;
        return true;
      } else {
        throw new Error(`Health check failed with status ${response.status}`);
      }
    } catch (error) {
      this.connectionStatus = ConnectionStatus.ERROR;
      this.consecutiveFailures++;

      if (API_CONFIG.showDebugInfo) {
        logger.error('[RepositoryService] Connection check failed:', { error });
      }

      return false;
    }
  }

  /**
   * List user's OAuth connections
   */
  async getConnections(): Promise<OAuthConnection[]> {
    try {
      const response = await api.get<{ connections: OAuthConnection[] }>('/oauth/connections');
      this.connectionStatus = ConnectionStatus.CONNECTED;
      this.consecutiveFailures = 0;
      return response.data.connections;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Connect/register OAuth provider with backend
   */
  async connectProvider(
    provider: 'github' | 'gitlab',
  ): Promise<{ message: string; connection: OAuthConnection }> {
    try {
      const response = await api.post<{ message: string; connection: OAuthConnection }>(
        `/oauth/${provider}/connect`,
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Sync repositories from a specific provider
   */
  async syncProvider(provider: 'github' | 'gitlab', connectionId?: string): Promise<SyncResult> {
    try {
      const response = await api.post<SyncResult>(
        `/oauth/${provider}/sync`,
        connectionId ? { connectionId } : undefined,
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Sync repositories from all connected providers
   */
  async syncAll(): Promise<SyncResult> {
    try {
      const response = await api.post<SyncResult>('/oauth/sync-all');
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Disconnect an OAuth connection
   */
  async disconnect(connectionId: string): Promise<{ message: string }> {
    try {
      const response = await api.delete<{ message: string }>(`/oauth/connections/${connectionId}`);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * List repositories with optional filters
   */
  async listRepositories(params?: {
    provider?: 'github' | 'gitlab';
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<RepositoryListResponse> {
    try {
      const response = await api.get<RepositoryListResponse>('/oauth/repositories', { params });
      this.connectionStatus = ConnectionStatus.CONNECTED;
      this.consecutiveFailures = 0;
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Get a single repository by ID
   */
  async getRepository(id: string): Promise<Repository> {
    try {
      const response = await api.get<{ repository: Repository }>(`/oauth/repositories/${id}`);
      return response.data.repository;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Check if user has any OAuth connections
   */
  async hasConnections(): Promise<boolean> {
    try {
      const connections = await this.getConnections();
      return connections.length > 0;
    } catch (error) {
      logger.error('Failed to check connections:', { error });
      return false;
    }
  }

  /**
   * Get connection status for a specific provider
   */
  async hasProvider(provider: 'github' | 'gitlab'): Promise<boolean> {
    try {
      const connections = await this.getConnections();
      return connections.some((conn) => conn.provider === provider);
    } catch (error) {
      logger.error('Failed to check provider:', { error });
      return false;
    }
  }

  /**
   * Sync with deduplication - returns existing promise if sync in progress
   * This prevents duplicate sync operations within the same tab
   */
  async syncProviderSafe(
    provider: 'github' | 'gitlab',
    connectionId?: string,
  ): Promise<SyncResult> {
    const key = connectionId ? `sync_${provider}_${connectionId}` : `sync_${provider}`;

    // Return existing promise if sync already in progress
    if (this.activeSyncs.has(key)) {
      return this.activeSyncs.get(key)!;
    }

    // Create new sync promise
    const syncPromise = this.syncProvider(provider, connectionId).finally(() => {
      // Clean up after completion (success or failure)
      this.activeSyncs.delete(key);
    });

    this.activeSyncs.set(key, syncPromise);
    return syncPromise;
  }

  /**
   * List organizations for a provider, with access status
   */
  async listOrganizations(provider: 'github' | 'gitlab'): Promise<OrganizationsResponse> {
    try {
      const response = await api.get<OrganizationsResponse>(`/oauth/${provider}/organizations`);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Check if sync is currently active for a provider
   */
  isSyncActive(provider: 'github' | 'gitlab', connectionId?: string): boolean {
    const key = connectionId ? `sync_${provider}_${connectionId}` : `sync_${provider}`;
    return this.activeSyncs.has(key);
  }

  /**
   * Handle errors and update connection status
   */
  private handleError(error: unknown): void {
    this.consecutiveFailures++;

    // Check if it's a connection error
    if (this.isConnectionError(error)) {
      this.connectionStatus = ConnectionStatus.ERROR;

      if (API_CONFIG.showDebugInfo) {
        logger.error('[RepositoryService] Connection error:', { error });
      }
    }
  }

  /**
   * Check if error is a connection-related error
   */
  private isConnectionError(error: unknown): boolean {
    if (!axios.isAxiosError(error)) {
      return false;
    }

    // Network errors from axios don't have a response
    if (!error.response) {
      return true;
    }

    const message = error.message?.toLowerCase() || '';
    return (
      error.code === 'ECONNABORTED' ||
      message.includes('network error') ||
      message.includes('timeout')
    );
  }
}

export const repositoryService = new RepositoryServiceClass();
